import { sortAsc } from '@strata/helper-functions';
import { validationStrings } from '@strata/strata-ui-strings';
import { getIn } from 'formik';
import * as Yup from 'yup';

export const ActivationTypes = {
    ON_CLAIM: 'ON_CLAIM',
    ON_START: 'ON_START',
    ON_DAY: 'ON_DAY',
};

export const FieldTypes = {
    TEXT: 'text',
    NUMBER: 'number',
    TEXT_AREA: 'text_area',
    SINGLE_SELECT: 'single_select',
    SINGLE_SELECT_OPTIONS: 'single_select_options',
    CHECKBOX: 'checkbox',
    DATE: 'date',
    RADIO_BUTTONS: 'radio_buttons',
    CHECKBOX_LIST: 'checkbox_list',
    FILE_UPLOAD: 'file_upload',
    MULTI_SELECT: 'multi_select',
};

export const ProductServiceTypes = {
    PRODUCT: 'Product',
    SERVICE: 'Service',
    THER_CAT: 'Therapeutic Category',
};

export const SubTaskConditionTypes = {
    MANUAL: 'MANUAL',
    AUTOMATIC: 'AUTOMATIC',
};

export const ConditionTypesForDisplay = {
    MANUAL: 'Manual',
    AUTOMATIC: 'Automatic',
};

export const subtaskTypesList = Object.keys(SubTaskConditionTypes).map((type) => ({
    text: ConditionTypesForDisplay[type],
    value: SubTaskConditionTypes[type],
}));

export const FundingSourcesForDisplay = {
    PHARMA: 'Pharma',
    PAYER: 'Payer',
    FMH: 'FMH',
};

export const FundingSourcesList = Object.entries(FundingSourcesForDisplay).map(([value, name]) => ({
    name,
    value,
}));

export const markdownLinksHelperText =
    'Markdown-like links may be used in the format of [text](url) and will be visible in the preview.';

export const checkProductServiceType = (oid) => {
    switch (oid.split('.')[1]) {
        case 'med': {
            return ProductServiceTypes.PRODUCT;
        }
        case 'visit': {
            return ProductServiceTypes.SERVICE;
        }
        default: {
            return ProductServiceTypes.THER_CAT;
        }
    }
};

export const isThereAnOnClaimTask = (tasksData) => {
    const manualTaskCount = tasksData.filter(
        (task) => task.activation_type === ActivationTypes.ON_CLAIM,
    ).length;
    return manualTaskCount > 0;
};

export const getId = ({ namespace, object }) => {
    if (object) {
        return getIn(object, `${namespace}_id`) ?? getIn(object, 'distinctId');
    }
    return undefined;
};

export const getElement = ({ distinctId, namespace, root, getAll = false }) => {
    const idKey = `${namespace}_id`;
    const elementsFound = [];
    const findElements = (object, path = '') => {
        const objectKeys = Object.keys(object);
        objectKeys.forEach((key) => {
            if ((key === 'distinctId' || key === idKey) && object[key] === distinctId) {
                elementsFound.push([structuredClone(object), `${path}.${key}`]);
            } else if (typeof object[key] === 'object' && object[key] != null) {
                if (Array.isArray(object[key])) {
                    object[key].forEach((element, index) => {
                        findElements(element, `${path}.${index}`);
                    });
                } else {
                    findElements(object[key], `${path}.${key}`);
                }
            }
        });
    };
    findElements(root);
    if (elementsFound.length === 0) {
        elementsFound.push([undefined, undefined]);
    }
    if (getAll) {
        return elementsFound;
    }
    elementsFound.sort(([, aPath], [, bPath]) => {
        const aLength = aPath?.split('.').length;
        const bLength = bPath?.split('.').length;
        return aLength - bLength;
    });

    return elementsFound[0];
};

export const getConditionDetails = (conditions = []) => {
    const conditionsCount = conditions?.length;
    const conditionsSelectedCount = conditions?.filter(
        (condition) => !!condition.condition_type,
    ).length;
    const manualConditions = conditions?.filter(
        (condition) => condition.condition_type === SubTaskConditionTypes.MANUAL,
    );
    const manualConditionsCount = manualConditions.length;
    const automaticConditions = conditions?.filter(
        (condition) => condition.condition_type === SubTaskConditionTypes.AUTOMATIC,
    );
    const automaticConditionsCount = automaticConditions.length;
    return {
        conditionsCount,
        conditionsSelectedCount,
        manualConditions,
        manualConditionsCount,
        automaticConditions,
        automaticConditionsCount,
    };
};

export const getConditionFilter = (formik, subtaskPath, uniqueId) => {
    const conditionsPath = `${subtaskPath}.conditions`;
    const conditions = getIn(formik.values, conditionsPath);
    const {
        conditionsCount,
        conditionsSelectedCount,
        manualConditions,
        manualConditionsCount,
        automaticConditions,
        automaticConditionsCount,
    } = getConditionDetails(conditions);
    if (conditionsCount > 1) {
        if (manualConditionsCount > 0) {
            if (conditionsSelectedCount === 1) {
                const firstManualSelectedConditionId = getId({
                    namespace: 'condition',
                    object: manualConditions[0],
                });
                if (firstManualSelectedConditionId !== uniqueId) {
                    return SubTaskConditionTypes.MANUAL;
                }
                return undefined;
            }
            return SubTaskConditionTypes.MANUAL;
        }
        if (automaticConditionsCount > 0) {
            if (conditionsSelectedCount === 1) {
                const firstAutomaticSelectedConditionId = getId({
                    namespace: 'condition',
                    object: automaticConditions[0],
                });
                if (firstAutomaticSelectedConditionId !== uniqueId) {
                    return SubTaskConditionTypes.AUTOMATIC;
                }
                return undefined;
            }
            return SubTaskConditionTypes.AUTOMATIC;
        }
    }
    return undefined;
};

/**
 * Method to check if the current string element in a Yup.array is unique.
 * May have performance implications with large set of options, O(N^2).
 */
export const addUniqueString = () =>
    Yup.addMethod(Yup.string, 'unique', function test(message) {
        return this.test('unique-test', message, function callback(current) {
            if ((this.parent ?? []).filter((option) => option === current).length > 1) {
                return this.createError({
                    path: this.path,
                    message,
                });
            }
            return true;
        });
    });

export const getParamsInitialValues = (conditionObjects) => {
    const initialValues = {};
    Object.keys(conditionObjects).forEach((condition) => {
        initialValues[condition] = conditionObjects[condition].default;
        if (
            conditionObjects[condition].type === FieldTypes.SINGLE_SELECT_OPTIONS &&
            !initialValues[condition]
        ) {
            initialValues[condition] = ['', ''];
        }
    });
    return initialValues;
};

export const getIsRequired = (yupType, isRequired, additionalSchemas = []) => {
    let newSchema = yupType;
    if (isRequired) {
        additionalSchemas.forEach(([schemaName, ...args]) => {
            newSchema = newSchema[schemaName](...args);
        });
        return newSchema.required(validationStrings.requiredField.errorMessage);
    }
    return yupType;
};

export const validationGetSibling = ({ siblings = [], index = 0, defaultReturn = undefined }) =>
    Array.isArray(siblings) && siblings.length > 0 ? siblings[index] : defaultReturn;

export const StopPropagation = {
    onClick: (e) => e.stopPropagation(),
    onKeyDown: (e) => e.stopPropagation(),
    role: 'presentation',
};

export const compareProp = (name) => (a, b) => sortAsc(a[name], b[name]);

export const convertYupPathToFormikPath = (yupPath) =>
    yupPath.replaceAll('[', '.').replaceAll(']', '');

export const setSupplierNpiOptions = (configurableParams, supplierNpiList) => {
    if ('supplier_npi' in configurableParams) {
        return {
            ...configurableParams,
            supplier_npi: {
                ...configurableParams.supplier_npi,
                options: supplierNpiList,
            },
        };
    }
    return configurableParams;
};
